import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star } from "lucide-react";
import { BUSINESS, WHATSAPP_LINK } from "../../data/site";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.35 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

const MaskLine = ({ children, i }) => (
  <span className="block overflow-hidden">
    <motion.span variants={line} custom={i} initial="hidden" animate="show" className="block">
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const photoY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const photoScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen pt-28 md:pt-32 pb-16 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-6 items-end">
          {/* Left: kinetic type */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="flex items-center gap-3 mb-6"
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-[#0A0A0A] px-3.5 py-1.5 text-xs font-medium font-body">
                <span className="h-2 w-2 rounded-full bg-[#00B4D8] animate-pulse" />
                {BUSINESS.location}, Portugal
              </span>
            </motion.div>

            <h1 className="font-display font-black leading-[0.85] tracking-tightest text-[#0A0A0A] text-[15vw] sm:text-[12vw] lg:text-[7rem] xl:text-[8rem]">
              <MaskLine i={0}>BANHOS</MaskLine>
              <MaskLine i={1}>
                <span className="text-magenta">&amp;</span> TOSQUIAS
              </MaskLine>
            </h1>

            <div className="overflow-hidden mt-6 max-w-md">
              <motion.p
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, delay: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="font-body text-base md:text-lg text-[#525252]"
              >
                Cuidamos do seu patudo com carinho, paciência e profissionalismo em Fernão Ferro.
              </motion.p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.1 }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-whatsapp"
                className="group inline-flex items-center gap-3 bg-[#0A0A0A] text-white font-body font-medium pl-6 pr-2.5 py-2.5 rounded-full"
              >
                Marcar agora
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#E20074] transition-transform duration-300 group-hover:rotate-45">
                  →
                </span>
              </a>
              <a
                href="#criticas"
                data-testid="hero-rating"
                className="inline-flex items-center gap-2.5"
              >
                <Star size={18} className="fill-[#E20074] text-[#E20074]" />
                <span className="font-display font-bold text-lg">{BUSINESS.rating}</span>
                <span className="font-body text-sm text-[#525252]">
                  · {BUSINESS.reviewCount} críticas Google
                </span>
              </a>
            </motion.div>
          </div>

          {/* Right: framed photo */}
          <div className="lg:col-span-5 order-1 lg:order-2">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="relative lg:translate-x-10 xl:translate-x-16"
            >
              <div className="relative border border-[#0A0A0A] bg-white p-2 rounded-[2px]">
                <div className="overflow-hidden rounded-[2px] aspect-[4/5]">
                  <motion.img
                    style={{ y: photoY, scale: photoScale }}
                    src={BUSINESS.heroPhoto}
                    alt="Cão tosquiado na Patolas"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>
              <motion.div
                initial={{ scale: 0, rotate: -20 }}
                animate={{ scale: 1, rotate: -12 }}
                transition={{ duration: 0.8, delay: 1.3, ease: [0.34, 1.56, 0.64, 1] }}
                className="absolute -top-6 -left-6 md:-left-10 h-24 w-24 md:h-28 md:w-28 rounded-full bg-[#00B4D8] flex items-center justify-center text-center"
              >
                <span className="font-script text-white text-xl md:text-2xl leading-tight font-bold">
                  Tratamento<br />VIP
                </span>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
